"use client";

import React, { useState, useEffect, useRef } from 'react';
import { 
  Box,
  Heading,
  Input,
  Button,
  Badge,
  Text,
  VStack,
  HStack,
  Flex,
  Textarea,
  IconButton,
  useColorModeValue,
  Grid,
  GridItem,
  Divider,
  useToast,
} from '@chakra-ui/react';
import { FiPlus, FiSend, FiTrash } from 'react-icons/fi';
import { getSha256 } from '@/utils/utils';
import { MQTTManager } from '@/services/mqttManager';
import { useTranslation } from 'react-i18next';

export interface Message {
  topic: string;
  payload: string;
  timestamp: Date;
}

const FRESHNESS_WINDOW_MS = 60 * 60 * 1000;

const parseNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const num = Number(value);
    return Number.isFinite(num) ? num : undefined;
  }
  return undefined;
};

const normalizeTimestampMs = (value: unknown): number | undefined => {
  const parsed = parseNumber(value);
  if (!parsed) return undefined;

  // If timestamp is seconds-based, convert to milliseconds.
  return parsed < 1_000_000_000_000 ? parsed * 1000 : parsed;
};

const isFreshTimestamp = (value: unknown): boolean | undefined => {
  const normalized = normalizeTimestampMs(value);
  if (!normalized) return undefined;
  return Date.now() - normalized <= FRESHNESS_WINDOW_MS;
};

const MqttUI: React.FC = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const [connected, setConnected] = useState(false);
  const [subscriptions, setSubscriptions] = useState<string[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newTopic, setNewTopic] = useState('parky/+/+/status');
  const [publishTopic, setPublishTopic] = useState('parky/device/867255072966163/command');
  const [publishMessage, setPublishMessage] = useState('{"command":"info","hardwareId":"867255072966163"}');
  const [connectionString, setConnectionString] = useState('wss://mqtt.letsparky.com');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const mqttManagerRef = useRef<MQTTManager | null>(null);

  // Color mode values
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const user_instance_hash = getSha256(
    window.navigator.userAgent +
    window.navigator.hardwareConcurrency +
    window.navigator.language
  );

  // Initialize MQTT manager without auto-connecting
  useEffect(() => {
    // Create MQTT manager
    const manager = new MQTTManager(
      connectionString,
      `panel_${user_instance_hash}`,
      "consumer",
      "rc>2uELJ9Av*"
    );
    
    // Set up callbacks
    manager.onConnect(() => {
      setConnected(true);
      toast({
        title: t('mqtt.toast.connectedTitle'),
        description: t('mqtt.toast.connectedDesc'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    });
    
    manager.onDisconnect(() => {
      setConnected(false);
      toast({
        title: t('mqtt.toast.disconnectedTitle'),
        description: t('mqtt.toast.disconnectedDesc'),
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    });
    
    manager.onMessage((topic, payload) => {
      let shouldKeepMessage = true;
      try {
        const parsedPayload = JSON.parse(payload) as { lastScan?: unknown; lastSeen?: unknown };
        const lastScanFresh = isFreshTimestamp(parsedPayload.lastScan);
        const lastSeenFresh = isFreshTimestamp(parsedPayload.lastSeen);

        if (typeof lastScanFresh === 'boolean' || typeof lastSeenFresh === 'boolean') {
          shouldKeepMessage = (lastScanFresh ?? false) || (lastSeenFresh ?? false);
        }
      } catch {
        // Non-JSON payloads continue to be displayed as-is.
      }

      if (!shouldKeepMessage) return;

      const message = {
        topic,
        payload,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, message]);
    });
    
    // Store reference without connecting
    mqttManagerRef.current = manager;
    
    // Cleanup on unmount
    return () => {
      if (mqttManagerRef.current) {
        mqttManagerRef.current.disconnect();
        mqttManagerRef.current = null;
      }
    };
  }, [connectionString, toast, t]);

  // Handle subscriptions
  useEffect(() => {
    if (!mqttManagerRef.current || !connected) return;
    
    // Subscribe to all topics
    subscriptions.forEach(topic => {
      mqttManagerRef.current?.subscribe(topic);
    });
    
  }, [connected, subscriptions]);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSubscribe = () => {
    if (!newTopic || !mqttManagerRef.current || subscriptions.includes(newTopic)) return;
    
    mqttManagerRef.current.subscribe(newTopic, (err) => {
      if (!err) {
        setSubscriptions([...subscriptions, newTopic]);
        toast({
          title: t('mqtt.toast.subscribedTitle'),
          description: t('mqtt.toast.subscribedDesc', { topic: newTopic }),
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } else {
        toast({
          title: t('mqtt.toast.subscribeFailedTitle'),
          description: t('mqtt.toast.subscribeFailedDesc', { topic: newTopic }),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    });
  };

  const handleUnsubscribe = (topic: string) => {
    if (!mqttManagerRef.current) return;
    
    mqttManagerRef.current.unsubscribe(topic);
    setSubscriptions(subscriptions.filter(t => t !== topic));
    toast({
      title: t('mqtt.toast.unsubscribedTitle'),
      description: t('mqtt.toast.unsubscribedDesc', { topic }),
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  const handlePublish = () => {
    if (!publishTopic || !mqttManagerRef.current) return;
    
    mqttManagerRef.current.publish(publishTopic, publishMessage);
    toast({
      title: t('mqtt.toast.publishedTitle'),
      description: t('mqtt.toast.publishedDesc', { topic: publishTopic }),
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const clearMessages = () => {
    setMessages([]);
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString();
  };

  const handleConnect = () => {
    if (mqttManagerRef.current && !connected) {
      mqttManagerRef.current.connect();
    }
  };

  const handleDisconnect = () => {
    if (mqttManagerRef.current) {
      mqttManagerRef.current.disconnect();
      setConnected(false);
    }
  };

  return (
    <Box>
      <Heading size="lg" mb={6}>{t('mqtt.title')}</Heading>
      
      <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6}>
        {/* Connection and Subscription Panel */}
        <GridItem>
          <VStack spacing={6} align="stretch">
            {/* Connection Card */}
            <Box
              borderWidth="1px"
              borderRadius="lg"
              borderColor={borderColor}
              bg={bg}
              p={6}
            >
              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="md">{t('mqtt.connection.title')}</Heading>
                <Badge 
                  colorScheme={connected ? "green" : "red"}
                  variant="solid"
                >
                  {connected ? t('mqtt.connection.connected') : t('mqtt.connection.disconnected')}
                </Badge>
              </Flex>
              
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>{t('mqtt.connection.brokerUrl')}</Text>
                  <HStack>
                    <Input 
                      value={connectionString} 
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConnectionString(e.target.value)}
                      placeholder={t('mqtt.brokerPlaceholder')}
                      isDisabled={connected}
                    />
                    {connected ? (
                      <Button 
                        onClick={handleDisconnect}
                        colorScheme="red"
                        minW="120px"
                      >
                        {t('mqtt.connection.disconnect')}
                      </Button>
                    ) : (
                      <Button 
                        onClick={handleConnect}
                        colorScheme="blue"
                        minW="120px"
                      >
                        {t('mqtt.connection.connect')}
                      </Button>
                    )}
                  </HStack>
                </Box>
              </VStack>
            </Box>

            {/* Subscriptions Card */}
            <Box
              borderWidth="1px"
              borderRadius="lg"
              borderColor={borderColor}
              bg={bg}
              p={6}
            >
              <Heading size="md" mb={4}>{t('mqtt.subscriptions')}</Heading>
              
              <VStack spacing={4} align="stretch">
                <HStack>
                  <Input 
                    value={newTopic} 
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewTopic(e.target.value)}
                    placeholder={t('mqtt.enterTopicToSubscribe')}
                    isDisabled={!connected}
                    onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && handleSubscribe()}
                  />
                  <Button 
                    onClick={handleSubscribe} 
                    isDisabled={!connected || !newTopic}
                    colorScheme="blue"
                    leftIcon={<FiPlus />}
                  >
                    {t('mqtt.subscribe')}
                  </Button>
                </HStack>
                
                <VStack spacing={2} align="stretch">
                  {subscriptions.length === 0 ? (
                    <Text fontSize="sm" color="gray.500">{t('mqtt.noActiveSubscriptions')}</Text>
                  ) : (
                    subscriptions.map((topic) => (
                      <Flex key={topic} justify="space-between" align="center" bg={cardBg} p={3} borderRadius="md">
                        <Text fontSize="sm" fontFamily="mono">{topic}</Text>
                        <IconButton 
                          size="sm" 
                          variant="ghost" 
                          aria-label={t('mqtt.unsubscribe')}
                          icon={<FiTrash />}
                          colorScheme="red"
                          onClick={() => handleUnsubscribe(topic)}
                        />
                      </Flex>
                    ))
                  )}
                </VStack>
              </VStack>
            </Box>

            {/* Publish Card */}
            <Box
              borderWidth="1px"
              borderRadius="lg"
              borderColor={borderColor}
              bg={bg}
              p={6}
            >
              <Heading size="md" mb={4}>{t('mqtt.publishCommand')}</Heading>
              
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>{t('mqtt.topic')}</Text>
                  <Input 
                    value={publishTopic} 
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPublishTopic(e.target.value)}
                    placeholder={t('mqtt.enterTopic')}
                    isDisabled={!connected}
                  />
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>{t('mqtt.message')}</Text>
                  <Textarea 
                    value={publishMessage} 
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setPublishMessage(e.target.value)}
                    placeholder={t('mqtt.enterMessage')}
                    isDisabled={!connected}
                    rows={3}
                  />
                </Box>
                <Button 
                  onClick={handlePublish} 
                  isDisabled={!connected || !publishTopic}
                  colorScheme="green"
                  leftIcon={<FiSend />}
                >
                  {t('mqtt.publish')}
                </Button>
              </VStack>
            </Box>
          </VStack>
        </GridItem>

        {/* Messages Panel */}
        <GridItem>
          <Box
            borderWidth="1px"
            borderRadius="lg"
            borderColor={borderColor}
            bg={bg}
            p={6}
            h="calc(100vh - 0px)"
            display="flex"
            flexDirection="column"
          >
            <Flex justify="space-between" align="center" mb={4}>
              <Heading size="md">{t('mqtt.messages.title')}</Heading>
              <Button 
                size="sm" 
                variant="outline"
                onClick={clearMessages}
                isDisabled={messages.length === 0}
                leftIcon={<FiTrash />}
              >
                {t('mqtt.messages.clear')}
              </Button>
            </Flex>
            
            <Box 
              flex="1" 
              overflowY="auto" 
              borderWidth="1px"
              borderRadius="md" 
              p={4} 
              bg={cardBg}
            >
              {messages.length === 0 ? (
                <Flex h="full" align="center" justify="center">
                  <Text color="gray.500">{t('mqtt.messages.noMessagesYet')}</Text>
                </Flex>
              ) : (
                <VStack spacing={3} align="stretch">
                  {messages.map((msg, index) => (
                    <Box key={index} bg={bg} borderWidth="1px" borderColor={borderColor} p={3} borderRadius="md" shadow="sm">
                      <Flex justify="space-between" align="start" mb={2}>
                        <Text fontSize="sm" fontWeight="medium" color="blue.500" fontFamily="mono">{msg.topic}</Text>
                        <Text fontSize="xs" color="gray.500">{formatTime(msg.timestamp)}</Text>
                      </Flex>
                      <Box bg={cardBg} p={2} borderRadius="md" overflowX="auto">
                        <Text fontSize="sm" fontFamily="mono" whiteSpace="pre-wrap" wordBreak="break-word">
                          {msg.payload}
                        </Text>
                      </Box>
                    </Box>
                  ))}
                  <div ref={messagesEndRef} />
                </VStack>
              )}
            </Box>
          </Box>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default MqttUI;